import {
    SelectedAttributes,
    generateDisabledAttributes,
    getSelectedVariant,
    isVariationSelected,
    getUnselectedOptions,
} from '@/store/attributes/attributes.utils';
import {ConfigurableProductOptions, ConfigurableVariant} from '@/__generated__/__types__';

type Action =
    | {type: 'SET_ATTRIBUTES'; attributes: SelectedAttributes}
    | {type: 'SET_INITIAL_DATA'; productOptions: ConfigurableProductOptions[]; variants: ConfigurableVariant[]}
    | {type: 'SET_IS_SELECTED'; isSelected: boolean};

export interface AttributesState {
    attributes: SelectedAttributes;
    isSelected: boolean;
    productOptions: ConfigurableProductOptions[];
    unselectedOptions: ConfigurableProductOptions[];
    variants: ConfigurableVariant[];
    selectedVariant: ConfigurableVariant | undefined;
    disabledAttributes: {[key: string]: Set<number>};
}

export const initialState: AttributesState = {
    attributes: {},
    isSelected: false,
    productOptions: [],
    variants: [],
    selectedVariant: undefined,
    disabledAttributes: {},
    unselectedOptions: [],
};

export function attributesReducer(state: AttributesState, action: Action): AttributesState {
    switch (action.type) {
        case 'SET_ATTRIBUTES': {
            return generateFinalState(state, action.attributes);
        }
        case 'SET_INITIAL_DATA': {
            const disabledAttributes = generateDisabledAttributes({}, [...action.productOptions], [...action.variants]);
            const unselectedOptions = getUnselectedOptions([...action.productOptions], {...state.attributes});

            return {
                ...state,
                productOptions: action.productOptions,
                variants: action.variants,
                disabledAttributes: disabledAttributes,
                unselectedOptions: unselectedOptions,
            };
        }
        case 'SET_IS_SELECTED': {
            return {
                ...state,
                isSelected: action.isSelected,
            };
        }
        default:
            return state;
    }
}

const generateFinalState = (state: AttributesState, attributes: SelectedAttributes): AttributesState => {
    const newAttributes = {...state.attributes, ...attributes};
    if (JSON.stringify(newAttributes) === JSON.stringify(state.attributes)) {
        return {
            ...state,
        };
    }
    const isAllAttributesSelected = isVariationSelected(state.productOptions, newAttributes);
    const disabledAttributes = generateDisabledAttributes(newAttributes, state.productOptions, state.variants);
    return {
        ...state,
        attributes: newAttributes,
        isSelected: isAllAttributesSelected,
        selectedVariant: isAllAttributesSelected ? getSelectedVariant(state.variants, newAttributes) : undefined,
        disabledAttributes: disabledAttributes,
        unselectedOptions: getUnselectedOptions(state.productOptions, newAttributes),
    };
};
